/**
 * triageEngine.js
 *
 * Deterministic, protocol-driven triage engine.
 *
 * API:
 *   triageEngine.evaluate(input) → TriageOutput
 *
 * Input shape:
 *   {
 *     protocolId: string,           // which protocol to run
 *     answers: {                    // question id → answered value
 *       [questionId: string]: boolean | number | string
 *     }
 *   }
 *
 * Output shape (TriageOutput):
 *   {
 *     careLevel: 'emergency_now' | 'care_today' | 'care_soon' | 'home_care',
 *     reasonCodes: string[],
 *     suggestedCareSites: string[],
 *     matchedRuleId: string | null,   // which rule fired ('redFlag:id' or 'rule:id')
 *     isDefault: boolean,             // true if no rule matched; fell through to defaultRule
 *   }
 */

import { getProtocol } from './protocolLoader';

// Care level numeric priority for conservative selection
const CARE_LEVEL_PRIORITY = {
  emergency_now: 4,
  care_today: 3,
  care_soon: 2,
  home_care: 1,
};

/**
 * Evaluate a single condition against the answers map.
 *
 * Supported operators: equals, gte, lte, gt, lt
 *
 * @param {{ question: string, equals?: *, gte?: number, lte?: number, gt?: number, lt?: number }} condition
 * @param {object} answers
 * @returns {boolean}
 */
function evaluateCondition(condition, answers) {
  const value = answers[condition.question];

  // If the answer hasn't been provided, treat the condition as not met
  if (value === undefined || value === null) return false;

  if ('equals' in condition) return value === condition.equals;
  if ('gte' in condition) return Number(value) >= condition.gte;
  if ('lte' in condition) return Number(value) <= condition.lte;
  if ('gt' in condition) return Number(value) > condition.gt;
  if ('lt' in condition) return Number(value) < condition.lt;

  return false;
}

/**
 * Evaluate all conditions in a rule's `if` array (AND logic).
 *
 * @param {object[]} conditions
 * @param {object} answers
 * @returns {boolean}
 */
function evaluateRule(conditions, answers) {
  if (!conditions || conditions.length === 0) return false;
  return conditions.every(c => evaluateCondition(c, answers));
}

/**
 * Main triage evaluation function.
 *
 * Evaluation order:
 *   1. Red flags (all checked; if multiple fire, highest priority wins)
 *   2. Rules (evaluated in order; first match wins)
 *   3. Default rule
 *
 * @param {{ protocolId: string, answers: object }} input
 * @returns {import('./triageEngine').TriageOutput}
 */
export function evaluate(input) {
  const { protocolId, answers = {} } = input;

  const protocol = getProtocol(protocolId);
  if (!protocol) {
    // Unknown protocol — conservative default
    return {
      careLevel: 'care_today',
      reasonCodes: ['unknown_protocol'],
      suggestedCareSites: ['urgent_care', 'primary_care'],
      matchedRuleId: null,
      isDefault: true,
    };
  }

  // ── 1. Red flags ──────────────────────────────────────────────────────────
  let bestRedFlag = null;

  for (const redFlag of protocol.redFlags || []) {
    if (evaluateRule(redFlag.if, answers)) {
      if (
        !bestRedFlag ||
        CARE_LEVEL_PRIORITY[redFlag.careLevel] >
          CARE_LEVEL_PRIORITY[bestRedFlag.careLevel]
      ) {
        bestRedFlag = redFlag;
      }
    }
  }

  if (bestRedFlag) {
    return {
      careLevel: bestRedFlag.careLevel,
      reasonCodes: bestRedFlag.reasonCodes || [],
      suggestedCareSites: bestRedFlag.suggestedCareSites || [],
      matchedRuleId: `redFlag:${bestRedFlag.id}`,
      isDefault: false,
    };
  }

  // ── 2. Urgency rules (first match) ────────────────────────────────────────
  for (const rule of protocol.rules || []) {
    if (evaluateRule(rule.if, answers)) {
      return {
        careLevel: rule.careLevel,
        reasonCodes: rule.reasonCodes || [],
        suggestedCareSites: rule.suggestedCareSites || [],
        matchedRuleId: `rule:${rule.id}`,
        isDefault: false,
      };
    }
  }

  // ── 3. Default rule ───────────────────────────────────────────────────────
  const def = protocol.defaultRule;
  return {
    careLevel: def.careLevel,
    reasonCodes: def.reasonCodes || [],
    suggestedCareSites: def.suggestedCareSites || [],
    matchedRuleId: null,
    isDefault: true,
  };
}

/**
 * Check whether any red flag fires for the current answers so far.
 * Used for early escalation during the question loop (exit early on
 * emergency_now without waiting for all questions).
 *
 * @param {{ protocolId: string, answers: object }} input
 * @returns {boolean}
 */
export function hasEmergencyRedFlag(input) {
  const { protocolId, answers = {} } = input;
  const protocol = getProtocol(protocolId);
  if (!protocol) return false;

  return (protocol.redFlags || []).some(
    rf => rf.careLevel === 'emergency_now' && evaluateRule(rf.if, answers)
  );
}

const triageEngine = { evaluate, hasEmergencyRedFlag };
export default triageEngine;
